/**
 * Per-browser cache for free-text translations, keyed by target language and a SHA-256 hash of
 * the source text. Backed by localStorage; display-only and best-effort — every access is guarded
 * so a blocked or full store degrades to a miss or a no-op, never a throw. Cached entries are
 * translated clinical free text, so {@link clearTranslationCache} MUST run on logout.
 */

const CACHE_PREFIX = "translation-cache:";
const CACHE_VERSION = "v1";

/**
 * Hex SHA-256 of a string via Web Crypto.
 *
 * @param value - The string to hash.
 * @returns The lowercase hex digest, or null when hashing is unavailable.
 */
const sha256Hex = async (value: string): Promise<string | null> => {
  try {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest))
      .map(byte => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
};

/**
 * Builds the storage key for a value.
 *
 * @param targetLanguage - The target language code.
 * @param text - The source text.
 * @returns The key, or null when the text cannot be hashed.
 */
const cacheKey = async (targetLanguage: string, text: string): Promise<string | null> => {
  const hash = await sha256Hex(text);
  return hash ? `${CACHE_PREFIX}${CACHE_VERSION}:${targetLanguage}:${hash}` : null;
};

/**
 * Reads a cached translation.
 *
 * @param targetLanguage - The target language code.
 * @param text - The source text.
 * @returns The cached translation, or null on a miss or any storage failure.
 */
export const getCachedTranslation = async (targetLanguage: string, text: string): Promise<string | null> => {
  try {
    const key = await cacheKey(targetLanguage, text);
    return key ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

/**
 * Stores a translation. Best-effort: a failure (quota, blocked storage) is swallowed.
 *
 * @param targetLanguage - The target language code.
 * @param text - The source text.
 * @param translated - The translated text to cache.
 */
export const setCachedTranslation = async (targetLanguage: string, text: string, translated: string): Promise<void> => {
  try {
    const key = await cacheKey(targetLanguage, text);
    if (key) {
      localStorage.setItem(key, translated);
    }
  } catch {
    // The cache is an optimization, not a requirement: ignore write failures.
  }
};

/**
 * Removes every cached translation. MUST be called on logout, since cached entries are
 * translated clinical free text.
 */
export const clearTranslationCache = (): void => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach(key => localStorage.removeItem(key));
  } catch {
    // Nothing we can do if storage is unavailable.
  }
};
